import type { LaddersStyle } from "@bing/engine-ladders";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BOARD_ASPECT } from "./geometry";
import { ScenePicture } from "./ScenePicture";
import { lookFor, type LaddersLook } from "./styles";

/* ------------------------------------------------------------------ *
 * مار پله — the SCENE (card-86; round 2, batch W1): the look's backdrop
 * (a picture, or «اورجینال»'s plain one), the board CENTRED on it, the
 * die's place right above the board, the temple's cut-out over the board's
 * corner. W2 supplies the board (`renderBoard`), the die (`renderDie`) and
 * the strip under it (`footer`). The Bing Games brand is GONE (name and
 * logo — the operator, round 2 item 4).
 *
 * ⚠️ MEASURED, NEVER VIEWPORT-ANCHORED. One ResizeObserver on the scene and
 * the stage feeds ONE pure function, `sceneLayout`, that places the board,
 * then the die, then the picture: moved so the temple's foot stands on the
 * 100 cell, and scaled — never below the board's own measured fit — until
 * it covers the WHOLE scene, the safe-area / Telegram header strip
 * included (FULL-BLEED, round 2 item 5: the white strip at the top was the
 * placeholder gradient's near-white stop showing above the picture). The
 * picture, the cut-out (same transform, above the board) and the board
 * cannot drift apart on any screen.
 *
 * ⚠️ NOTHING IS MIRRORED: `dir="ltr"` on the scene, physical left/top
 * only. 100 stays top-left under the temple (a CSS grid would follow
 * `dir="rtl"` and move it top-right, away from the temple).
 *
 * ⚠️ THE SCENE OWNS THE SAFE AREAS (top and bottom insets): the footer
 * must not pad them again. The scene itself is inset 0 — under Telegram's
 * header buttons — and only its column is padded.
 *
 * ⚠️ NOTHING HERE MOVES: no animation, no transition. The picture's load
 * fade is `ScenePicture`'s (batch P), once.
 * ------------------------------------------------------------------ */

/** What the scene tells its footer: the column's width in CSS px (0 before the first measure).
 *  Measured from the stage, so it never depends on the footer's own height. */
export interface LaddersSceneFit {
  readonly width: number;
}

export interface LaddersSceneProps {
  /** `view.style` — the look (an unknown one draws jungle). */
  readonly style: LaddersStyle;
  /** The board, drawn in the centred board slot at `sizePx` (the square's side). */
  readonly renderBoard: (sizePx: number) => ReactNode;
  /** The die, drawn in its place right ABOVE the board, centred on it: a `sizePx` square, ≥ 44. */
  readonly renderDie: (sizePx: number) => ReactNode;
  /** W2: seat strip, hint, timer — or a function of the column's fit. */
  readonly footer: ReactNode | ((fit: LaddersSceneFit) => ReactNode);
  /** W2: the shell's overlays (exit, chat toggle). */
  readonly children?: ReactNode;
}

/** The board's square never runs closer than this to the column's sides. */
export const SCENE_MARGIN_X = 8;
/** Room between the board (its start pad) and the footer. */
export const SCENE_GAP_BELOW = 6;
/** The board's largest side, on any screen. */
export const SCENE_MAX_SIDE = 520;
/**
 * The top of the stage that W2's header strip owns — the exit and the chat toggle, 40 px buttons
 * 10 px under the safe top inset (as on every table) — in STAGE px: the stage starts 8 px under the
 * inset, so the strip ends at 10 + 40 − 8 = 42, plus a 4 px gap. The die never enters it (at
 * 240×533 the centred die would sit on the chat toggle). Keep in step with W2's strip.
 */
export const SCENE_HEADER_RESERVE = 10 + 40 - 8 + 4;
/** The table column's width (every table's: `max-w-[440px]`). */
export const SCENE_COLUMN_MAX = 440;
/** The die's place: never smaller than this (a thumb's target)… */
export const DIE_SLOT_MIN = 44;
/** …never larger than this, and otherwise this share of the board's side. */
export const DIE_SLOT_MAX = 52;
export const DIE_SLOT_SHARE = 0.13;
/** Between the die's place and the board's frame — at least this; a taller screen gives more. */
export const DIE_GAP = 6;
/**
 * Of the room ABOVE the board (`SLACK_ABOVE_*`), this share goes between the die and the board —
 * the rest between the header strip and the die (the operator, 2026-11-04: «Move the die's area a
 * bit higher, so there's more space between it and the board»).
 */
export const DIE_GAP_SHARE = 0.5;
/** The die stays at least this far right of the temple's box. */
export const DIE_TEMPLE_GAP = 4;
/**
 * Of a tall screen's spare height, this share goes ABOVE the die and the board, the rest UNDER the
 * player strip — the die, the board and the strip stand together, centred…
 */
export const SLACK_ABOVE_SHARE = 0.5;
/** …but never more than this above — the more room above the board, the more the picture grows. */
export const SLACK_ABOVE_MAX = 56;
/**
 * The player strip (the scene's `footer`) sits this far under the board's start pad — raised to
 * it, never left at the bottom of a tall screen (the operator, 2026-11-04: «Move the player panel
 * at the bottom … a bit closer to the board»). Its height is its own (fixed during play).
 */
export const FOOTER_GAP = SCENE_GAP_BELOW;
/**
 * The most the picture may grow past the board's own measured fit to cover the scene's WIDTH (a
 * Desktop window far wider than the column): past it, its sides feather into the placeholder
 * instead — the temple would otherwise swamp the board's corner. The HEIGHT is always covered.
 */
export const COVER_MAX_X = 1.75;
/** The cover overshoots each edge by this much, so no hairline of the placeholder shows. */
export const COVER_BLEED = 1;

export interface SceneBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** What `sceneLayout` is measured against — CSS px. */
export interface SceneFrame {
  /** The stage: the column between the padded top and the footer. */
  readonly stageW: number;
  readonly stageH: number;
  /** The stage's top-left inside the scene (the full-bleed root). */
  readonly offX: number;
  readonly offY: number;
  /** The whole scene (the viewport, header strip included). */
  readonly sceneW: number;
  readonly sceneH: number;
}

export interface SceneLayout {
  /** The board's square side (`sizePx`). */
  readonly side: number;
  /** The board element (square + start pad), in stage px. */
  readonly board: SceneBox;
  /** The die's place (a square), in stage px. */
  readonly die: SceneBox;
  /** Source px → CSS px (0 for a look without a picture). */
  readonly scale: number;
  /** How far the picture grew past the board's own measured fit to cover the scene (≥ 1). */
  readonly cover: number;
  /** The whole picture, in stage px (it reaches past the stage: it covers the scene). */
  readonly bg: SceneBox | null;
  /** The temple's cut-out, drawn over the board. */
  readonly temple: SceneBox | null;
  /** The temple's box's right edge (stage px) — the die stays clear of it. */
  readonly templeRight: number | null;
  /** The picture covers the scene's whole width (else its sides feather into the placeholder). */
  readonly coversWidth: boolean;
  /** How far the footer is RAISED from the column's bottom, to sit {@link FOOTER_GAP} under the
   *  board (CSS px, ≥ 0) — a static transform: the stage, measured, never moves for it. */
  readonly footerLift: number;
}

/** The die's place for a board `side` px wide. */
export function dieSlotFor(side: number): number {
  return Math.round(Math.min(DIE_SLOT_MAX, Math.max(DIE_SLOT_MIN, side * DIE_SLOT_SHARE)));
}

/**
 * Where everything goes. Pure: the tests drive it at every device size the operator checks.
 *
 *   1. the largest side such that the header strip, the die's place, the board (+ its start pad)
 *      and the gap above the footer fit the stage height, and the square fits the width;
 *   2. the board is CENTRED in the column; a tall screen's spare height goes half above
 *      (≤ {@link SLACK_ABOVE_MAX}) and the rest under the player strip, which is RAISED to sit
 *      {@link FOOTER_GAP} under the board; the die sits above the board, centred on it — the room
 *      above shared between its gap to the board and its gap to the header strip — nudged right
 *      only where the temple's box would touch it. The side never pays for any of it: it is the
 *      largest that fits with the least gaps;
 *   3. a picture look: the picture is moved so its temple's foot (the spire's x, the cut-out's
 *      lowest edge) stands where the board's measured fit puts it — over the 100 cell — and scaled
 *      about that point, never below that fit, until it covers the whole scene (its height always;
 *      its width up to {@link COVER_MAX_X}); the cut-out rides the same transform.
 */
export function sceneLayout(frame: SceneFrame, look: LaddersLook): SceneLayout {
  const { stageW, stageH } = frame;
  const minTop = (side: number): number => SCENE_HEADER_RESERVE + dieSlotFor(side) + DIE_GAP;
  const fits = (side: number): boolean =>
    minTop(side) + side * BOARD_ASPECT + SCENE_GAP_BELOW <= stageH;
  let lo = 0;
  let hi = Math.max(0, Math.min(SCENE_MAX_SIDE, stageW - 2 * SCENE_MARGIN_X));
  if (fits(hi)) lo = hi;
  else {
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid;
      else hi = mid;
    }
  }
  const side = Math.floor(lo);
  const dieSize = dieSlotFor(side);
  const slack = Math.max(0, stageH - (minTop(side) + side * BOARD_ASPECT + SCENE_GAP_BELOW));
  const above = Math.min(slack * SLACK_ABOVE_SHARE, SLACK_ABOVE_MAX);
  const boardTop = Math.round(minTop(side) + above);
  const boardLeft = Math.round((stageW - side) / 2);
  const board = { left: boardLeft, top: boardTop, width: side, height: side * BOARD_ASPECT };
  const dieTop = boardTop - (DIE_GAP + Math.round(above * DIE_GAP_SHARE)) - dieSize;
  const footerLift = Math.max(0, stageH - (boardTop + board.height + FOOTER_GAP));
  const centred = Math.round(boardLeft + (side - dieSize) / 2);

  if (look.kind !== "picture" || side <= 0) {
    return {
      side,
      board,
      die: { left: centred, top: dieTop, width: dieSize, height: dieSize },
      scale: 0,
      cover: 1,
      bg: null,
      temple: null,
      templeRight: null,
      coversWidth: true,
      footerLift,
    };
  }

  const S = look.scene;
  const B = S.board;
  const fit = side / B.w;
  // The fixed point: the temple's foot — over the 100 cell, where the cut-out meets the board.
  const fx = S.band.spireX;
  const fy = S.temple ? S.temple.y + S.temple.h : B.y;
  const Fx = boardLeft + (fx - B.x) * fit;
  const Fy = boardTop + (fy - B.y) * fit;
  // The scene, in stage px, plus the bleed.
  const left = -frame.offX - COVER_BLEED;
  const top = -frame.offY - COVER_BLEED;
  const right = frame.sceneW - frame.offX + COVER_BLEED;
  const bottom = frame.sceneH - frame.offY + COVER_BLEED;
  const needY = Math.max((Fy - top) / fy, (bottom - Fy) / (S.master.h - fy));
  const needX = Math.max((Fx - left) / fx, (right - Fx) / (S.master.w - fx));
  const s = Math.max(fit, needY, Math.min(needX, fit * COVER_MAX_X));
  const bg = { left: Fx - fx * s, top: Fy - fy * s, width: S.master.w * s, height: S.master.h * s };
  const temple = S.temple
    ? {
        left: bg.left + S.temple.x * s,
        top: bg.top + S.temple.y * s,
        width: S.temple.w * s,
        height: S.temple.h * s,
      }
    : null;
  const templeRight = bg.left + S.band.templeRight * s;
  // Centred on the board — unless the temple's box would touch it: then just clear of it.
  const dieLeft = Math.round(
    Math.min(Math.max(centred, templeRight + DIE_TEMPLE_GAP), boardLeft + side - dieSize),
  );
  return {
    side,
    board,
    die: { left: dieLeft, top: dieTop, width: dieSize, height: dieSize },
    scale: s,
    cover: s / fit,
    bg,
    temple,
    templeRight,
    coversWidth: s >= needX - 1e-9,
    footerLift,
  };
}

interface Measured {
  readonly w: number;
  readonly h: number;
  readonly offX: number;
  readonly offY: number;
  readonly sw: number;
  readonly sh: number;
}

const same = (a: Measured, b: Measured): boolean =>
  a.w === b.w &&
  a.h === b.h &&
  a.offX === b.offX &&
  a.offY === b.offY &&
  a.sw === b.sw &&
  a.sh === b.sh;

export function LaddersScene({
  style,
  renderBoard,
  renderDie,
  footer,
  children,
}: LaddersSceneProps): ReactNode {
  const look = lookFor(style);
  const sceneRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<Measured | null>(null);
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return undefined;
    // ONE observer, two targets: the scene (the screen) and the stage (the footer, the insets). A
    // callback per resize — never a loop; nothing runs while nothing changes.
    const measure = (): void => {
      const a = scene.getBoundingClientRect();
      const b = stage.getBoundingClientRect();
      const next = {
        w: stage.clientWidth,
        h: stage.clientHeight,
        offX: Math.round((b.left - a.left) * 100) / 100,
        offY: Math.round((b.top - a.top) * 100) / 100,
        sw: scene.clientWidth,
        sh: scene.clientHeight,
      };
      setBox((prev) => (prev && same(prev, next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(scene);
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);
  // Laid out once per measure and look — a game's frames re-render the scene, never re-solve it.
  const L = useMemo(
    () =>
      box && box.w > 0 && box.h > 0
        ? sceneLayout(
            {
              stageW: box.w,
              stageH: box.h,
              offX: box.offX,
              offY: box.offY,
              sceneW: box.sw,
              sceneH: box.sh,
            },
            look,
          )
        : null,
    [box, look],
  );
  const fit: LaddersSceneFit = { width: box?.w ?? 0 };

  return (
    <div
      ref={sceneRef}
      dir="ltr"
      data-testid="ladders-scene"
      data-style={look.id}
      className={`ladders-scene ladders-scene--${look.kind}`}
      style={{
        position: "relative",
        height: "var(--game-h)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // A picture look: its edge colours, until the picture has loaded (`ScenePicture` fades it
        // in) — then the picture covers all of it. «اورجینال»: its class's plain backdrop.
        background: look.kind === "picture" ? look.placeholder : undefined,
      }}
    >
      <div
        style={{
          position: "relative",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: SCENE_COLUMN_MAX,
          margin: "0 auto",
          paddingTop: "calc(8px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
          paddingBottom: "var(--game-safe-bottom)",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={stageRef}
          data-testid="ladders-stage"
          style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}
        >
          {L ? (
            <>
              {look.kind === "picture" && L.bg ? (
                <ScenePicture
                  jpg={look.bg.jpg}
                  {...(look.bg.webp ? { webp: look.bg.webp } : {})}
                  box={L.bg}
                  className={
                    L.coversWidth
                      ? "ladders-scene__bg"
                      : "ladders-scene__bg ladders-scene__bg--feather"
                  }
                  testId="ladders-bg"
                />
              ) : null}
              <div
                data-testid="ladders-board-slot"
                style={{
                  position: "absolute",
                  left: L.board.left,
                  top: L.board.top,
                  width: L.board.width,
                  height: L.board.height,
                }}
              >
                {renderBoard(L.side)}
              </div>
              {/* The temple's base, OVER the board's corner — the same transform as the picture. */}
              {look.kind === "picture" && look.temple && L.temple ? (
                <ScenePicture
                  jpg={look.temple.jpg}
                  {...(look.temple.webp ? { webp: look.temple.webp } : {})}
                  box={L.temple}
                  className="ladders-scene__temple"
                  testId="ladders-temple"
                />
              ) : null}
              {/* The die's place: right above the board, centred on it — a clear place of its own. */}
              <div
                data-testid="ladders-die-place"
                className="ladders-die-place"
                style={{
                  position: "absolute",
                  left: L.die.left,
                  top: L.die.top,
                  width: L.die.width,
                  height: L.die.height,
                }}
              >
                {renderDie(L.die.width)}
              </div>
            </>
          ) : null}
        </div>
        <div
          data-testid="ladders-footer"
          style={{
            position: "relative",
            flex: "none",
            zIndex: 3,
            // Raised to sit under the board — a static transform, so the measured stage never moves.
            transform: L && L.footerLift > 0 ? `translateY(${-L.footerLift}px)` : undefined,
          }}
        >
          {typeof footer === "function" ? footer(fit) : footer}
        </div>
      </div>
      {children}
    </div>
  );
}
